/**
 * Pure helpers for the Events workspace (no React, no network), so they can be
 * pinned by tests/events.test.mjs: which page a /events path is, the feed's
 * URL filters, tag terms, merging live events into a feed, chart rows.
 */

/** Path segments under /events that are pages, never project slugs (the API reserves them too). */
export const RESERVED_SEGMENTS = new Set(["e", "settings", "notifications", "new", "v1", "map", "feed"])

export type EventsSection = "feed" | "detail" | "insights" | "users" | "user" | "settings" | "notifications" | "map"

export interface EventsRoute {
  section: EventsSection
  project: string | null
  ulid?: string
  userId?: string
}

/** Which Events page a pathname is. Unknown subpaths fall back to the feed. */
export function parseEventsPath(pathname: string): EventsRoute {
  const parts = pathname.replace(/\/+$/, "").split("/").filter(Boolean)
  if (parts[0] !== "events") return { section: "feed", project: null }
  const [, first, second, third] = parts.map((s) => decodeURIComponent(s))
  if (!first) return { section: "feed", project: null }
  if (first === "e" && second) return { section: "detail", project: null, ulid: second }
  if (first === "settings") return { section: "settings", project: null }
  if (first === "notifications") return { section: "notifications", project: null }
  if (first === "map") return { section: "map", project: null }
  if (RESERVED_SEGMENTS.has(first)) return { section: "feed", project: null }
  // Charts merged into Insights, and Funnels went, on 2026-10-22; both old URLs redirect there.
  if (second === "insights" || second === "charts" || second === "funnels") return { section: "insights", project: first }
  if (second === "users" && third) return { section: "user", project: first, userId: third }
  if (second === "users") return { section: "users", project: first }
  if (second === "map") return { section: "map", project: first }
  return { section: "feed", project: first }
}

export interface FeedFilters {
  channel: string
  q: string
  user: string
  tags: string[]
}

export const EMPTY_FILTERS: FeedFilters = { channel: "", q: "", user: "", tags: [] }

/** Feed filters from the URL: ?channel= &q= &user= &tag=k:v (repeatable). */
export function filtersFromSearch(search: URLSearchParams | string): FeedFilters {
  const sp = typeof search === "string" ? new URLSearchParams(search) : search
  return {
    channel: sp.get("channel") ?? "",
    q: sp.get("q") ?? "",
    user: sp.get("user") ?? "",
    tags: sp.getAll("tag").filter((t) => parseTag(t) !== null),
  }
}

/** The URL query for a set of filters; empty values are left out. */
export function searchFromFilters(f: FeedFilters): string {
  const sp = new URLSearchParams()
  if (f.channel) sp.set("channel", f.channel)
  if (f.q) sp.set("q", f.q)
  if (f.user) sp.set("user", f.user)
  for (const t of f.tags) sp.append("tag", t)
  const s = sp.toString()
  return s ? `?${s}` : ""
}

export function hasFilters(f: FeedFilters): boolean {
  return !!(f.channel || f.q || f.user || f.tags.length)
}

/** "plan:pro" → {key, value}. The value may contain colons; the key may not be empty. */
export function parseTag(term: string): { key: string; value: string } | null {
  const i = term.indexOf(":")
  if (i <= 0 || i === term.length - 1) return null
  return { key: term.slice(0, i).trim(), value: term.slice(i + 1).trim() }
}

/** A tag as a filter term ("company:Acme Staffing"); the API takes it verbatim in tag[]. */
export function tagTerm(key: string, value: string | number | boolean): string {
  return `${key}:${String(value)}`
}

/** Toggle a tag filter on or off. */
export function toggleTag(tags: string[], term: string): string[] {
  return tags.includes(term) ? tags.filter((t) => t !== term) : [...tags, term]
}

/** Feed order: newest first by occurred_at, then by id (ULIDs sort by time) as the tiebreak. */
export function compareEvents(a: { id: string; occurred_at?: string }, b: { id: string; occurred_at?: string }): number {
  const ta = a.occurred_at ? Date.parse(a.occurred_at) : 0
  const tb = b.occurred_at ? Date.parse(b.occurred_at) : 0
  if (ta !== tb) return tb - ta
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0
}

/**
 * Live events on top of the paged feed, deduped by id and sorted (occurred_at
 * desc, id desc). The live poll re-returns an overlap window before its
 * cursor, and a backfilled event can be older than ones already shown, so
 * order is restored by sorting rather than by trusting arrival order.
 */
export function mergeEvents<T extends { id: string; occurred_at?: string }>(fresh: T[], pages: T[]): T[] {
  const byId = new Map<string, T>()
  for (const e of [...fresh, ...pages]) if (!byId.has(e.id)) byId.set(e.id, e)
  return [...byId.values()].sort(compareEvents)
}

/** Add a poll's events to the live list (deduped, sorted). */
export function prependFresh<T extends { id: string; occurred_at?: string }>(current: T[], incoming: T[]): T[] {
  return mergeEvents(incoming, current)
}

/** Ids in a poll that have never been shown: only these animate in. */
export function unseenIds(known: ReadonlySet<string>, incoming: { id: string }[]): string[] {
  return incoming.filter((e) => !known.has(e.id)).map((e) => e.id)
}

export interface SeriesLike {
  series: { key: string; label: string; points: { t: string; v: number }[] }[]
}

/** Recharts rows: one object per timestamp with a column per series key. */
export function seriesToRows(s: SeriesLike): Array<Record<string, number | string>> {
  const rows = new Map<string, Record<string, number | string>>()
  for (const line of s.series) {
    for (const pt of line.points) {
      const row = rows.get(pt.t) ?? { t: pt.t }
      row[line.key] = ((row[line.key] as number | undefined) ?? 0) + pt.v
      rows.set(pt.t, row)
    }
  }
  const keys = s.series.map((l) => l.key)
  return [...rows.values()]
    .sort((a, b) => String(a.t).localeCompare(String(b.t)))
    .map((row) => {
      for (const k of keys) if (row[k] === undefined) row[k] = 0
      return row
    })
}

/** Insight values: numbers get thousands separators and at most 2 decimals. */
export function formatInsightValue(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—"
  if (typeof value === "number") return value.toLocaleString("en-US", { maximumFractionDigits: 2 })
  const n = Number(value)
  return value.trim() !== "" && Number.isFinite(n) ? n.toLocaleString("en-US", { maximumFractionDigits: 2 }) : value
}

/** Date range for a chart's `range_days`, as YYYY-MM-DD in the viewer's day. */
export function rangeDates(rangeDays: number, now = new Date()): { from: string; to: string } {
  const to = new Date(now)
  const from = new Date(now)
  from.setDate(from.getDate() - Math.max(1, rangeDays) + 1)
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
  return { from: iso(from), to: iso(to) }
}

/** A "mute for" choice → the ISO time it ends (null = unmute). */
export function muteUntil(hours: number | null, now = new Date()): string | null {
  if (hours === null) return null
  return new Date(now.getTime() + hours * 3600_000).toISOString()
}

export function isMuted(until: string | null | undefined, now = new Date()): boolean {
  return !!until && new Date(until).getTime() > now.getTime()
}

/** The project shown when /events has none in its path: the last one opened. */
export const LAST_PROJECT_KEY = "forge-events-last-project"

export function readLastProject(storage: Pick<Storage, "getItem"> | null): string | null {
  try {
    return storage?.getItem(LAST_PROJECT_KEY) || null
  } catch {
    return null
  }
}

export function writeLastProject(storage: Pick<Storage, "setItem"> | null, slug: string): void {
  try {
    storage?.setItem(LAST_PROJECT_KEY, slug)
  } catch {
    // Private mode or blocked storage: the feed just opens on "All projects".
  }
}

/**
 * Senders write markdown with single newlines ("**Plan:** Pro\n**Reason:** …")
 * and expect line breaks, as LogSnag shows them. Turn single newlines into
 * hard breaks outside fenced code; blank lines stay paragraph breaks.
 */
export function softBreaks(md: string): string {
  return md
    .split(/(```[\s\S]*?```)/g)
    .map((part, i) => (i % 2 === 1 ? part : part.replace(/([^\n])\n(?!\n)/g, "$1  \n")))
    .join("")
}

/** A curl that sends a first event, for empty states and the token dialog. */
export function curlExample(baseUrl: string, token = "$FORGE_EVENTS_TOKEN", channel = "general"): string {
  return [
    `curl -sS ${baseUrl}/log \\`,
    `  -H "Authorization: Bearer ${token}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '{"channel":"${channel}","event":"Hello from Forge","icon":"👋","notify":true}'`,
  ].join("\n")
}

